import { useState, useEffect } from "react"
import { readFile } from "../../../helpers/imageHelpers"
import { v4 as uuidv4 } from "uuid"
import { getVideoDuration } from "../../../helpers"
import { source } from "../../../services/axios"
import services from "../../../services"

const useUploadVideo = () => {
  const [uploadedVideos, setUploadedVideos] = useState([])
  const [uploadedVideosUrl, setUploadedVideosUrl] = useState([])
  const [loading, setLoading] = useState(0)
  const [error, setError] = useState("")
  const maxWeight = 40000000 // 40 mB

  const clearUploadedVideos = () => {
    setUploadedVideos([])
    setUploadedVideosUrl([])
    setError("")
  }

  const onUploadVideo = async (videos, { target }) => {
    const files = Array.from(target.files)
    
    if (files.length === 0) {
      return
    }
    
    const currentTotalSize = uploadedVideos.reduce((total, video) => {
      return total + (video.file?.size || 0)
    }, 0)
    
    const newFilesTotalSize = files.reduce((total, file) => {
      return total + (file.size || 0)
    }, 0)
    
    const totalSize = currentTotalSize + newFilesTotalSize
    
    if (totalSize > maxWeight) {
      const currentSizeMB = (currentTotalSize / (1024 * 1024)).toFixed(2)
      const newSizeMB = (newFilesTotalSize / (1024 * 1024)).toFixed(2)
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2)
      setError(
        `Total file size exceeds 40MB limit. Current videos: ${currentSizeMB}MB, New files: ${newSizeMB}MB, Total: ${totalSizeMB}MB. Maximum allowed total size is 40MB. Please remove some files.`
      )
      target.value = ""
      return
    }
    
    if (videos.length + files.length + uploadedVideos.length > 12) {
      setError("Sorry, you can't add more than 12 videos.")
      target.value = ""
      return
    }
    
    setError("")
    
    const validatedFiles = files.filter((file, index) => {
      if (index >= 12) {
        return false
      }
      if (videos.length + files.length + uploadedVideos.length > 12) {
        return false
      }
      return true
    })
    
    if (validatedFiles.length === 0) {
      return
    }

    const processedFiles = await Promise.all(
      validatedFiles.map(async (file) => {
        return {
          id: uuidv4(),
          name: file.name,
          length: await getVideoDuration(file),
          file,
        }
      }),
    )

    const validProcessedFiles = processedFiles.filter((file) => {
      return file !== null && file.file
    })
    
    if (validProcessedFiles.length === 0) {
      setError("No valid videos to add.")
      target.value = ""
      return
    }
    
    const newTotalSize = [...uploadedVideos, ...validProcessedFiles].reduce((total, video) => {
      return total + (video.file?.size || 0)
    }, 0)
    
    if (newTotalSize > maxWeight) {
      const totalSizeMB = (newTotalSize / (1024 * 1024)).toFixed(2)
      setError(
        `Total file size exceeds 40MB limit. Total size would be ${totalSizeMB}MB. Maximum allowed total size is 40MB. Please remove some files.`
      )
      target.value = ""
      return
    }

    const videoUrls = await Promise.all(
      validProcessedFiles.map(async (file) => {
        return {
          id: file.id,
          name: file.name,
          length: file.length,
          path: await readFile(file.file),
        }
      }),
    )

    setUploadedVideos([...uploadedVideos, ...validProcessedFiles])
    setUploadedVideosUrl([...uploadedVideosUrl, ...videoUrls])
    target.value = ""
  }

  const handlePopupMediaDelete = (id) => {
    const newVideosUrl = uploadedVideosUrl.filter((video) => video.id !== id)
    const newVideoFiles = uploadedVideos.filter((video) => video.id !== id)
    setUploadedVideosUrl(newVideosUrl)
    setUploadedVideos(newVideoFiles)
    
    const remainingOversized = newVideoFiles.filter((video) => {
      if (!video || !video.file) {
        return false
      }
      const fileSize = video.file.size || 0
      return fileSize > maxWeight
    })
    if (remainingOversized.length === 0) {
      setError("")
    }
  }
  
  const hasOversizedVideos = () => {
    if (!uploadedVideos || uploadedVideos.length === 0) {
      return false
    }
    const totalSize = uploadedVideos.reduce((total, video) => {
      return total + (video.file?.size || 0)
    }, 0)
    return totalSize > maxWeight
  }
  
  const getTotalSize = () => {
    if (!uploadedVideos || uploadedVideos.length === 0) {
      return 0
    }
    return uploadedVideos.reduce((total, video) => {
      return total + (video.file?.size || 0)
    }, 0)
  }
  
  const getOversizedVideosInfo = () => {
    if (!uploadedVideos || uploadedVideos.length === 0) {
      return null
    }
    const totalSize = getTotalSize()
    if (totalSize <= maxWeight) {
      return null
    }
    
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2)
    const videoSizes = uploadedVideos.map((v) => {
      const fileSize = v.file?.size || 0
      return `${v.name} (${(fileSize / (1024 * 1024)).toFixed(2)}MB)`
    }).join(", ")
    
    return { 
      totalSize, 
      totalSizeMB, 
      sizes: videoSizes, 
      count: uploadedVideos.length 
    }
  }

  const handleUploadMedia = async () => {
    if (uploadedVideos.length === 0) {
      throw new Error("No videos to upload")
    }
    
    const totalSize = getTotalSize()
    if (totalSize > maxWeight) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2)
      const errorMsg = `Total file size exceeds 40MB limit. Total size is ${totalSizeMB}MB. Maximum allowed total size is 40MB. Please remove some files and try again.`
      setError(errorMsg)
      throw new Error(errorMsg)
    }
    
    setError("")
    setLoading(0)
    
    const options = {
      onUploadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent
        let percentage = Math.floor((loaded * 100) / total)
        setLoading(percentage)
      },
      cancelToken: source.token,
    }

    try {
      const result = await services.userServices.addVideoToGallery(
        uploadedVideos,
        options,
      )
      setLoading(100)
      clearUploadedVideos()
      return result
    } catch (error) {
      setLoading(0)
      throw error
    }
  }

    return {
    uploadedVideos,
    loading,
    error,
    setLoading,
    setError,
    clearUploadedVideos,
    onUploadVideo,
    uploadedVideosUrl,
    handlePopupMediaDelete,
    handleUploadMedia,
    hasOversizedVideos,
    getOversizedVideosInfo,
    getTotalSize,
  }
}

export default useUploadVideo
